import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AcquiringSettings,
  RefillStep,
  RefillTarget,
  RefillType,
  asStepNumber,
} from 'app/dataTypes/acquiring/settings'
import {
  useAcquiringItemByID,
  useAcquiringItemsByRefillType,
} from 'app/dataProviders/acquiring/settings/hooks'

import RefillLoading from './RefillLoading'
import useAmountState from './useAmountState'
import Step0 from './Step0'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Step5 from './Step5'
import { BankCardId } from 'app/dataTypes/acquiring/recurrent/cards'

type Props = {
  payForAlerts?: boolean
  cafeteriaOwnAccountRefillDisable?: boolean
  acquiringSettings: AcquiringSettings
  refillStep: RefillStep | null
  refillTarget: RefillTarget | null
  refillType: RefillType | null
  targetID: string | null
  loading: boolean
  successURL: string
  failureURL: string
  hideReplenishChild?: boolean
  hideReplenishChildCamp?: boolean
  onChangeStep: (props: {
    step: number
    target?: RefillTarget | null
    type?: RefillType | null
    targetID?: string
  }) => void
}

const Refill = ({
  payForAlerts,
  cafeteriaOwnAccountRefillDisable,
  acquiringSettings,
  refillStep,
  refillTarget,
  refillType,
  targetID,
  loading,
  successURL,
  failureURL,
  hideReplenishChild,
  hideReplenishChildCamp,
  onChangeStep,
}: Props) => {
  const { t } = useTranslation('finances')
  const { amount, changeAmount } = useAmountState()
  const [recurrent, setRecurrent] = useState<
    { bankId: BankCardId } | { save: boolean } | {} | null
  >(null)

  const acquiringItems = useAcquiringItemsByRefillType(acquiringSettings, refillType)
  const acquiringItem = useAcquiringItemByID(targetID, acquiringItems)

  const handlePreviosStep = useCallback(() => {
    const currentStepNumber = asStepNumber(refillStep)
    onChangeStep({
      step:
        currentStepNumber === 2 && refillTarget === RefillTarget.Self
          ? currentStepNumber - 2
          : currentStepNumber - 1,
    })
  }, [onChangeStep, refillStep, refillTarget])

  const handleNextStep = useCallback(
    (trgtID?: string) => {
      const currentStepNumber = asStepNumber(refillStep)
      onChangeStep({ step: currentStepNumber + 1, targetID: trgtID })
    },
    [onChangeStep, refillStep],
  )

  const handleRefillChild = useCallback(
    (type: RefillType) => {
      onChangeStep({
        step: 1,
        target: RefillTarget.Child,
        type,
      })
    },
    [onChangeStep],
  )

  const handleRefillSelf = useCallback(
    (type: RefillType) => {
      onChangeStep({
        step: 1,
        target: RefillTarget.Self,
        type,
      })
    },
    [onChangeStep],
  )

  const getUnnamedHeader = useCallback(
    (ID: string) => {
      if (refillType === RefillType.Cafeteria && refillTarget === RefillTarget.Child) {
        return t('child_selector_all_children', 'Всем детям')
      }
      return `Пополнить счет пользователя с идентификатором ${ID}`
    },
    [refillType, refillTarget, t],
  )

  if (loading) {
    return <RefillLoading itemsCount={2} />
  }

  return (
    <>
      {!refillStep && (
        <Step0
          payForAlerts={payForAlerts}
          cafeteriaOwnAccountRefillDisable={cafeteriaOwnAccountRefillDisable}
          acquiringSettings={acquiringSettings}
          hideReplenishChild={hideReplenishChild}
          hideReplenishChildCamp={hideReplenishChildCamp}
          onClickRefillChild={handleRefillChild}
          onClickRefillSelf={handleRefillSelf}
        />
      )}

      {refillStep === RefillStep.Step1 && (
        <Step1
          acquiringItems={acquiringItems}
          getUnnamedHeader={getUnnamedHeader}
          refillTarget={refillTarget}
          onPrev={handlePreviosStep}
          onNext={handleNextStep}
        />
      )}

      {refillStep === RefillStep.Step2 && (
        <Step2
          refillTarget={refillTarget}
          refillType={refillType}
          amount={amount}
          acquiringItem={acquiringItem}
          changeAmount={changeAmount}
          onPrev={handlePreviosStep}
          onNext={handleNextStep}
        />
      )}

      {refillStep === RefillStep.Step3 && (
        <Step3
          amount={amount}
          acquiringItem={acquiringItem}
          onPrev={handlePreviosStep}
          onNext={handleNextStep}
        />
      )}

      {refillStep === RefillStep.Step4 && (
        <Step4
          amount={amount}
          acquiringItem={acquiringItem}
          refillType={refillType}
          successURL={successURL}
          failureURL={failureURL}
          onRecurrent={setRecurrent}
          onPrev={handlePreviosStep}
          onNext={handleNextStep}
        />
      )}

      {refillStep === RefillStep.Step5 && (
        <Step5
          amount={amount}
          acquiringItem={acquiringItem}
          recurrent={recurrent}
          refillType={refillType}
          successURL={successURL}
          failureURL={failureURL}
          onPrev={handlePreviosStep}
        />
      )}
    </>
  )
}

export default Refill
